import type { ChatClaimDisclaimerItem, ChatWidgetItem } from "@health/ui/components/chat/types";
import type { ExtractorToolPart } from "../../chat/chat-tool-config";

/**
 * `save_intake_fields` inline-widgets extractor. Projects the
 * `output.inline_widgets` array (added in F3 Option B) into a list of
 * `ChatWidgetItem`s plus any `claim-disclaimer` footer items.
 *
 * The save's output may contain inline widgets the SDK should render
 * directly without the model emitting a separate widget tool call. Today
 * the only producer is the discovery claim path on chatless-v1 + MC v1
 * variants — the model used to chain `save_intake_fields → show_claim`,
 * costing ~5–7s of model RTT for a tool-only second step. With the inline
 * payload, the discovery turn collapses to a single model step.
 *
 * Co-located with `SaveIntakeFields.transition.ts` so all `save_intake_fields`
 * projections live in one place. Returns empty arrays — never null — to
 * keep the call site at `normalize.ts` simple.
 */

type InlineWidgetEntry = {
  tool_name: string;
  input: Record<string, unknown>;
};

function parseInlineWidgets(raw: unknown): InlineWidgetEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: InlineWidgetEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.tool_name !== "string" || !e.tool_name) continue;
    if (!e.input || typeof e.input !== "object") continue;
    out.push({ tool_name: e.tool_name, input: e.input as Record<string, unknown> });
  }
  return out;
}

export function extractSaveIntakeInlineWidgets(
  messageId: string,
  partIdx: number,
  toolPart: ExtractorToolPart,
): { widgetItems: ChatWidgetItem[]; footerItems: ChatClaimDisclaimerItem[] } {
  const empty = { widgetItems: [], footerItems: [] };
  if (toolPart.state !== "output-available" || !toolPart.output) return empty;

  const output = toolPart.output as Record<string, unknown>;
  const entries = parseInlineWidgets(output.inline_widgets);
  if (entries.length === 0) return empty;

  const widgetItems: ChatWidgetItem[] = [];
  const footerItems: ChatClaimDisclaimerItem[] = [];

  entries.forEach((entry, idx) => {
    const baseId = `${messageId}-${partIdx}-inline-${idx}`;
    widgetItems.push({
      kind: "widget",
      id: `${baseId}-widget`,
      toolName: entry.tool_name,
      toolCallId: baseId,
      input: entry.input,
      state: "submitted",
    });

    if (entry.tool_name === "show_claim") {
      const claim = entry.input.claim as { disclaimer?: unknown } | undefined;
      const disclaimer =
        claim && typeof claim.disclaimer === "string" && claim.disclaimer.trim()
          ? claim.disclaimer
          : null;
      if (disclaimer) {
        footerItems.push({
          kind: "claim-disclaimer",
          id: `${baseId}-claim-disclaimer`,
          disclaimer,
        });
      }
    }
  });

  return { widgetItems, footerItems };
}
